/* ============================================================================
   EL DUTY TIME SALE DEL LIBRO

   Era el último eslabón. `horasVueloJornada` se tecleaba a mano en Operaciones
   **mientras el libro del piloto ya tenía las horas reales**.

   Eso no es solo trabajo duplicado: es una **segunda versión de la verdad**. Si
   los dos números difieren, nadie se entera, y en una auditoría las dos cifras
   están.

   ══════════════════════════════════════════════════════════════════════════
   LAS CUATRO REGLAS, LAS MISMAS QUE EL FRAT

   1. **El libro manda mientras el piloto no conteste a mano.** Y cuando
      contesta, su respuesta no se pisa: él sabe si voló algo que la aplicación
      no registró.

   2. **Lo derivado se ve distinto de lo escrito.** Una cifra que salió del
      libro y una que alguien recordó no valen lo mismo, y el registro tiene
      que poder decir cuál es cuál.

   3. **Un dato ausente NUNCA baja la jornada.** Si el libro no tiene vuelos de
      hoy, el resultado es `null` —«sin registrar»—, no cero. Cero significa
      «no voló», que es una afirmación, y una afirmación falsa en horas de
      servicio deja volar a alguien que ya no puede.

   4. **Si el piloto declara MÁS horas que el libro, se acepta sin discutir.**
      Pudo volar en otra aeronave, o con otro operador. Si declara MENOS, se
      avisa: el libro tiene asientos que él no está contando.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import { totalesDeLibro, esVuelo } from "./bitacoraPiloto.js";

const num = (v) => (Number.isFinite(v) ? v : null);

/** De dónde salió la cifra de la jornada. */
export const FUENTE = {
  libro: "libro",
  manual: "manual",
  ausente: "ausente",
};

/**
 * Horas de vuelo de un piloto en una fecha, según el libro.
 *
 * Devuelve `null` si no hay asientos de ese día: no es lo mismo que cero.
 */
export function horasDelDia(libro, pilotId, fechaISO) {
  const lista = Array.isArray(libro) ? libro : [];
  const fecha = String(fechaISO || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return null;

  /* ═══ UN ARRASTRE NO SE VOLÓ ESE DÍA ═══

     El arrastre lleva la fecha HASTA la que cubre, no la de un vuelo. Si esa
     fecha de corte cae en el día que se está evaluando, sus horas entraban a
     la jornada como si el piloto las hubiera volado hoy.

     Con un arrastre de carrera eso daba **12202 horas de jornada** contra un
     límite de 8, y el panel marcaba INTOLERABLE sobre un piloto que no había
     volado nada.

     La señal estaba a la vista y nadie la miraba: «12202 h en 0 vuelos de ese
     día». Horas sin vuelos es exactamente lo que produce un arrastre. */
  const delDia = lista.filter((a) => (
    a?.pilotId === pilotId
    && String(a?.fecha).slice(0, 10) === fecha
    && esVuelo(a)
  ));
  if (delDia.length === 0) return null;

  const t = totalesDeLibro(delDia, {});
  return { horas: t.totalHoras, vuelos: t.vuelos, hhmm: t.totalHhMm };
}

/**
 * La cifra de jornada que corresponde, y de dónde salió.
 *
 * @param {Array} libro         asientos del libro del piloto
 * @param {string} pilotId
 * @param {string} fechaISO     el día de la jornada
 * @param {*} declarado         lo que el piloto escribió, si escribió algo
 */
export function horasDeJornada(libro, pilotId, fechaISO, declarado = null) {
  const delLibro = horasDelDia(libro, pilotId, fechaISO);

  /* Lo declarado a mano MANDA. El piloto sabe si voló algo que la aplicación
     no registró —otra aeronave, otro operador—, y pisarlo sería descartar lo
     que él sabe por lo que la base tiene. */
  const crudo = typeof declarado === "string" ? declarado.trim() : declarado;
  const manual = (crudo === null || crudo === undefined || crudo === ""
    || !Number.isFinite(Number(crudo)))
    ? null
    : Number(crudo);

  const avisos = [];

  if (manual !== null) {
    if (delLibro && manual + 0.05 < delLibro.horas) {
      /* Declaró MENOS que el libro. No se corrige solo —puede haber un motivo—
         pero el registro tiene que decirlo: si nadie lo mira, un piloto podría
         quedar por debajo de su jornada real. */
      avisos.push(`Declaró ${manual} h pero el libro tiene ${delLibro.horas} h `
        + `en ${delLibro.vuelos} ${delLibro.vuelos === 1 ? "vuelo" : "vuelos"} de ese día. `
        + "Revisa cuál es la cifra correcta.");
    }
    return {
      horas: manual,
      fuente: FUENTE.manual,
      delLibro: delLibro ? delLibro.horas : null,
      avisos,
    };
  }

  if (!delLibro) {
    /* Regla 3: sin asientos, `null`. Cero significaría «no voló», que es una
       afirmación, y una afirmación falsa en horas de servicio deja volar a
       alguien que ya no puede. */
    return {
      horas: null,
      fuente: FUENTE.ausente,
      delLibro: null,
      avisos: ["Sin vuelos registrados en el libro para ese día. Si voló, "
        + "cargalo o declaralo a mano: sin este dato la jornada no se puede evaluar."],
    };
  }

  return {
    horas: delLibro.horas,
    fuente: FUENTE.libro,
    delLibro: delLibro.horas,
    vuelos: delLibro.vuelos,
    avisos,
  };
}

/**
 * Texto para mostrar junto a la cifra.
 *
 * Devuelve `null` cuando es manual sin nada que aclarar: una etiqueta que no
 * agrega información ocupa lugar y se aprende a ignorar.
 */
export function explicacionDeJornada(resultado) {
  if (!resultado) return null;

  if (resultado.fuente === FUENTE.libro) {
    return `Del libro: ${resultado.vuelos} ${resultado.vuelos === 1 ? "vuelo" : "vuelos"} de ese día.`;
  }
  if (resultado.fuente === FUENTE.ausente) return "Sin vuelos registrados.";
  if (resultado.delLibro !== null) {
    return `Declarado a mano. El libro tiene ${resultado.delLibro} h.`;
  }
  return "Declarado a mano.";
}

/**
 * Duty time de todos los pilotos para una fecha.
 *
 * Lo que necesita el jefe de operaciones de un vistazo: quién voló cuánto hoy,
 * de dónde salió cada cifra, y dónde hay que mirar.
 */
export function jornadaDeTodos(libro, pilotos = [], fechaISO, declarados = {}) {
  const lista = Array.isArray(pilotos) ? pilotos : [];

  return lista.map((p) => {
    const id = p?.id ?? p;
    const r = horasDeJornada(libro, id, fechaISO, declarados?.[id]?.horasVueloJornada);
    return {
      id,
      nombre: p?.nombre ?? String(id),
      ...r,
      explicacion: explicacionDeJornada(r),
    };
  });
}
